/**
 * Widget Templates Library
 * Pre-defined widget templates with various themes, placements, and features
 */

import type { WidgetTemplate } from '../types';
import {
  DEFAULT_THEME_STYLES,
  DEFAULT_PLACEMENT_CONFIG,
  DEFAULT_ICON_CONFIG,
  DEFAULT_BADGE_CONFIG,
  DEFAULT_ANIMATION_CONFIG,
  DEFAULT_BRAND_CONFIG,
  DEFAULT_BEHAVIOR_CONFIG,
} from './default-values';

export const WIDGET_TEMPLATES: WidgetTemplate[] = [
  // Chat Preview Widget - Modern chat bubble with expandable chat window
  {
    id: 'tpl-chat-preview',
    name: 'Chat Preview',
    description:
      'Modern chat bubble with expandable chat window and welcome message. Perfect for engaging visitors with a friendly conversational interface.',
    thumbnail: '/assets/widget-templates/chat-preview.png',
    category: 'general',
    theme: 'modern',
    placement: 'bottom-right',
    featureType: 'chat-preview',
    themeStyles: {
      ...DEFAULT_THEME_STYLES,
      primaryColor: '#25D366',
      secondaryColor: '#128C7E',
      backgroundColor: '#FFFFFF',
      textColor: '#1F2937',
      headerColor: '#25D366',
      fontFamily: '"Readex Pro", system-ui, sans-serif',
      useGradient: false,
      gradientDirection: '135deg',
    },
    placementConfig: {
      ...DEFAULT_PLACEMENT_CONFIG,
      type: 'bottom-right',
      marginBottom: 24,
      marginRight: 24,
      animationType: 'slide',
      animationDuration: 300,
    },
    featureConfig: {
      type: 'chat-preview',
      showChatHistory: true,
      previewMessageCount: 1,
    },
    iconConfig: {
      ...DEFAULT_ICON_CONFIG,
      type: 'whatsapp',
      animation: 'none',
    },
    badgeConfig: {
      ...DEFAULT_BADGE_CONFIG,
      type: 'count',
      text: '1',
    },
    brandConfig: DEFAULT_BRAND_CONFIG,
    behaviorConfig: DEFAULT_BEHAVIOR_CONFIG,
    animationConfig: {
      ...DEFAULT_ANIMATION_CONFIG,
      type: 'slide',
      enablePulse: false,
      enableNudge: false,
    },
    isPremium: false,
    isPopular: true,
    isNew: false,
    usageCount: 0,
    rating: 5.0,
    tags: ['chat', 'preview', 'whatsapp', 'modern', 'conversational'],
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
];

/**
 * Get template by ID
 */
export const getTemplateById = (id: string): WidgetTemplate | undefined => {
  return WIDGET_TEMPLATES.find((template) => template.id === id);
};

/**
 * Filter templates by criteria
 */
export const filterTemplates = (
  templates: WidgetTemplate[],
  filters: {
    theme?: string;
    placement?: string;
    featureType?: string;
    category?: string;
    isPremium?: boolean | null;
    search?: string;
  }
): WidgetTemplate[] => {
  return templates.filter((template) => {
    if (filters.theme && filters.theme !== 'all' && template.theme !== filters.theme) {
      return false;
    }
    if (filters.placement && filters.placement !== 'all' && template.placement !== filters.placement) {
      return false;
    }
    if (filters.featureType && filters.featureType !== 'all' && template.featureType !== filters.featureType) {
      return false;
    }
    if (filters.category && filters.category !== 'all' && template.category !== filters.category) {
      return false;
    }
    if (filters.isPremium !== null && filters.isPremium !== undefined && template.isPremium !== filters.isPremium) {
      return false;
    }
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      const matchesName = template.name.toLowerCase().includes(searchLower);
      const matchesDescription = template.description.toLowerCase().includes(searchLower);
      const matchesTags = template.tags.some((tag) => tag.toLowerCase().includes(searchLower));
      if (!matchesName && !matchesDescription && !matchesTags) {
        return false;
      }
    }
    return true;
  });
};

/**
 * Sort templates by criteria
 */
export const sortTemplates = (
  templates: WidgetTemplate[],
  sortBy: 'popular' | 'newest' | 'rating' | 'name'
): WidgetTemplate[] => {
  const sorted = [...templates];
  switch (sortBy) {
    case 'popular':
      return sorted.sort((a, b) => b.usageCount - a.usageCount);
    case 'newest':
      return sorted.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
    case 'rating':
      return sorted.sort((a, b) => b.rating - a.rating);
    case 'name':
      return sorted.sort((a, b) => a.name.localeCompare(b.name));
    default:
      return sorted;
  }
};
